import React, { FC } from 'react';
import { styled } from 'styled-components';
import { IConfig } from '../types/config';
import { IConfigArticlesPage } from '../types/config';
import { IArticle } from '../types/article';
import { IPaginationData } from '../components/Pagination';
import { IGlobaVariables } from '../components/Layout/styles';

import Layout from './../components/Layout';
import SEO from './../components/Seo';
import Breadcrumbs, { IBreadcrumb } from '../sections/BreadCrumbs';
import ArticleBox from '../components/ArticleBox';
import CategoriesSide from '../sections/CategoriesSide';
import Pagination from '../components/Pagination';
import ArticlesRecommended from '../sections/ArticlesRecommended';
import { breadcrumbs, common, routing } from '../translates';

const Styles = styled.div<{
  theme?: IGlobaVariables;
  $config: IConfigArticlesPage;
}>`
  .articles {
    &-column-wrapper {
      position: relative;
      display: flex;
      gap: 30px;
      flex-direction: column;
    }

    &-header {
      color: ${({ $config: styles }) => styles.group_header.color};
      font-family: ${({ $config: styles }) => styles.group_header.fontFamily};
      font-weight: ${({ $config: styles }) => styles.group_header.fontWeight};
      font-size: ${({ $config: styles }) => styles.group_header.fontSize};
      line-height: ${({ $config: styles }) => styles.group_header.lineHeight};
      border-bottom: ${({ $config: styles }) => styles.group_header.borderBottom};
      text-align: ${({ $config: styles }) => styles.group_header.textAlign};
      padding-bottom: ${({ $config: styles }) => (styles.group_header.borderBottom === 'none' ? 0 : '12px')};
      margin-bottom: 40px;
    }

    &-left-column {
      width: 100%;
      flex-shrink: 0;
    }

    &-right-column {
      width: 100%;
      flex-shrink: 0;
    }

    &-list {
      display: grid;
      gap: 50px 30px;
      grid-template-columns: 1fr;
      margin-bottom: 50px;
    }
  }

  @media (min-width: ${({ theme }) => theme.breakpoint.md}) {
    .articles {
      &-list {
        grid-template-columns: 1fr 1fr;
      }
    }
  }

  @media (min-width: ${({ theme }) => theme.breakpoint.lg}) {
    .articles {
      &-column-wrapper {
        flex-direction: row;
      }

      &-left-column {
        width: calc(66.6666% - 15px);
      }

      &-right-column {
        width: calc(33.3333% - 15px);
      }
    }
  }
`;

const Articles: FC<{
  pageContext: {
    siteConfig: IConfig;
    articles: IArticle[];
    articlesPopular: IArticle[];
    categories: string[];
    paginationData: IPaginationData;
    activeCategory?: string;
    language: string;
  };
}> = ({
  pageContext: { siteConfig, articles, categories, paginationData, activeCategory = '', articlesPopular, language },
}) => {
  const breadcrumbsItems = activeCategory
    ? [{ label: breadcrumbs[language].articles, link: routing[language].articles + '/' }, { label: activeCategory }]
    : [{ label: breadcrumbs[language].articles }];
  return (
    <Layout pageName="articles" language={language} config={siteConfig} categories={categories}>
      <Breadcrumbs language={language} items={breadcrumbsItems} config={siteConfig} />
      <div className="container">
        <Styles $config={siteConfig.articles}>
          <h1 className="articles-header">
            {activeCategory
              ? `${common[language].sectionArticlesByCategory}: ${activeCategory}`
              : common[language].sectionArticles}
          </h1>
          <div className="articles-column-wrapper">
            <div className="articles-left-column">
              <div className="articles-list">
                {articles.length
                  ? articles.map((article) => (
                      <ArticleBox
                        key={article.id}
                        language={language}
                        config={siteConfig}
                        article={article}
                        withCategory
                        activeCategory={activeCategory}
                      />
                    ))
                  : common[language].noArticles}
              </div>
              {paginationData ? <Pagination config={siteConfig} paginationData={paginationData} /> : null}
            </div>
            <div className="articles-right-column">
              {categories.length ? (
                <CategoriesSide
                  language={language}
                  config={siteConfig}
                  categories={categories}
                  activeCategory={activeCategory}
                />
              ) : null}
              {articlesPopular.length ? (
                <ArticlesRecommended language={language} config={siteConfig} articles={articlesPopular} />
              ) : null}
            </div>
          </div>
        </Styles>
      </div>
    </Layout>
  );
};

export default Articles;

export const Head: FC<{
  pageContext: {
    siteConfig: IConfig;
    activeCategory?: string;
    googleSiteVerification: string;
    iconUrl: string;
    language: string;
    rootUrl?: string;
  };
}> = ({ pageContext: { siteConfig, activeCategory = '', googleSiteVerification, iconUrl, language, rootUrl } }) => {
  let breadcrumbsSchema: IBreadcrumb[] =
    activeCategory && rootUrl
      ? [
          {
            label: breadcrumbs[language].articles,
            link: `${rootUrl}${routing[language].articles}`,
          },
          { label: activeCategory },
        ]
      : [
          {
            label: breadcrumbs[language].articles,
          },
        ];

  return (
    <>
      <html lang={`${language?.toLowerCase()}-${language?.toUpperCase()}`} />
      {googleSiteVerification ? <meta name="google-site-verification" content={googleSiteVerification} /> : null}
      <SEO
        meta={activeCategory ? siteConfig.general.meta.articlesCategory : siteConfig.general.meta.articles}
        breadcrumbsSchema={breadcrumbsSchema}
        fonts={siteConfig.general.content.fonts}
        gtmCode={siteConfig.general.gtm}
        iconUrl={iconUrl}
      />
    </>
  );
};
